import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Eyebrow } from "@/components/landing/Eyebrow";
import { Typewriter, CountUp } from "@/components/landing/Motion";
import { HOOK, WEBINAR, formatWebinarDate } from "@/lib/content";

export const Hero = ({ settings, onCtaClick }) => {
  const remaining = settings?.seats_remaining ?? "—";
  const total = settings?.total_seats ?? "—";
  const webinarTime = settings?.webinar_time || "20:00";
  const originalPrice = settings?.original_price_hkd || 1980;
  const showPrice = settings?.show_original_price ?? true;
  const taken = settings?.seats_taken ?? 0;
  const dateLabel = formatWebinarDate(settings?.webinar_date, "short", WEBINAR.dateLabel);

  return (
    <section className="relative overflow-hidden bg-ink min-h-[100svh] flex flex-col">
      <div className="absolute inset-0 starfield opacity-70" aria-hidden />
      <div className="absolute inset-0 hero-grid opacity-50" aria-hidden />
      <div className="absolute inset-0 spotlight-xl" aria-hidden />

      <div className="relative container px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto pt-10 pb-10 sm:pt-20 sm:pb-16 lg:pt-24 lg:pb-20 flex-1 flex flex-col justify-center">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center"
          >
            <Eyebrow label="SYSTEM" sub="ONLINE" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-tc mt-5 sm:mt-8 text-[38px] leading-[1.1] sm:text-6xl lg:text-[76px] font-bold tracking-[-0.02em] text-[hsl(var(--foreground))]"
          >
            <span className="block">{HOOK.title1}</span>
            <span className="block mt-1.5 sm:mt-2 min-h-[1.1em]">
              <Typewriter text={HOOK.title2} speed={110} startDelay={500} className="lime-keyword" />
            </span>
            <span className="block mt-1.5 sm:mt-2">{HOOK.title3}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="mt-4 sm:mt-8 mx-auto max-w-[48ch] text-[14px] sm:text-lg leading-relaxed text-[hsl(var(--muted-foreground))] px-2"
          >
            {HOOK.subtitle}
          </motion.p>

          {/* Pills */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-5 sm:mt-6 flex flex-wrap items-center justify-center gap-2"
          >
            <div
              data-testid="webinar-seats-remaining-badge"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[rgba(212, 180, 106,0.35)] bg-[rgba(212, 180, 106,0.06)]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--lime))] animate-pulse" />
              <span className="text-[11px] sm:text-xs text-[hsl(var(--foreground))]/90">名額剩</span>
              <span className="font-num font-bold text-[hsl(var(--lime))] text-sm">{remaining}</span>
              <span className="text-[11px] sm:text-xs text-[hsl(var(--muted-foreground))]">/ {total}</span>
            </div>
            {showPrice && (
              <div className="inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-full border border-[rgba(255,255,255,0.12)]">
                <span className="font-num text-[11px] sm:text-xs text-[hsl(var(--muted-foreground))] line-through">
                  HKD {originalPrice.toLocaleString()}
                </span>
                <span className="text-[10px] sm:text-[11px] font-semibold text-[hsl(var(--lime))] uppercase tracking-wider">限時免費</span>
              </div>
            )}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2 }}
            className="mt-6 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <Button
              data-testid="webinar-cta-hero-button"
              size="lg"
              onClick={onCtaClick}
              className="btn-lime btn-lime-pulse w-full sm:w-auto h-14 px-7 text-[16px] sm:text-[17px] font-bold"
            >
              {WEBINAR.ctaCopy}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            {/* Secondary button only on desktop */}
            <a
              href="#speaker"
              className="btn-outline-soft hidden sm:inline-flex h-12 px-5 text-sm items-center justify-center"
            >
              了解更多
            </a>
          </motion.div>

          {/* Social proof */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.28 }}
            className="mt-5 sm:mt-7 flex items-center justify-center gap-2.5"
          >
            <AvatarStack />
            <span className="text-[11px] sm:text-[13px] text-[hsl(var(--muted-foreground))]">
              已有 <span className="text-[hsl(var(--foreground))] font-semibold font-num"><CountUp value={String(Math.max(12, taken + 18))} duration={1.2} /></span>+ 位專家報名
            </span>
          </motion.div>

          {/* Meta */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.34 }}
            className="mt-4 sm:mt-6 text-[11px] sm:text-sm text-[hsl(var(--muted-foreground))] font-num"
          >
            <span data-testid="hero-date">{dateLabel}</span>
            <span className="mx-1.5 sm:mx-2 opacity-50">|</span>
            <span data-testid="hero-time">{webinarTime}（港）</span>
            <span className="mx-1.5 sm:mx-2 opacity-50">|</span>
            <span>Zoom · 2 小時</span>
          </motion.div>
        </div>
      </div>

      {/* Bottom stats — compact on mobile */}
      <div className="relative container px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto pb-8 sm:pb-14">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid grid-cols-3 rounded-xl sm:rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] backdrop-blur-sm"
        >
          <QuickStat raw="10億+" label="操盤流量" />
          <QuickStat raw="300+" label="學員孵化" bordered />
          <QuickStat raw="2000萬+" label="精準流量" />
        </motion.div>
      </div>

      {/* Scroll indicator — only on sm+ (mobile Sticky CTA takes its place) */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 pointer-events-none hidden sm:block">
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center text-[hsl(var(--muted-foreground))]/60"
        >
          <span className="text-[9px] tracking-[0.3em] uppercase">SCROLL</span>
          <ChevronDown className="w-4 h-4 mt-0.5" />
        </motion.div>
      </div>
    </section>
  );
};

const QuickStat = ({ raw, label, bordered }) => (
  <div className={`py-3.5 sm:py-5 text-center ${bordered ? "border-x border-[rgba(255,255,255,0.08)]" : ""}`}>
    <div className="stat-num text-[22px] sm:text-4xl text-[hsl(var(--foreground))]">
      <CountUp value={raw} duration={1.4} />
    </div>
    <div className="mt-1 text-[10px] sm:text-xs text-[hsl(var(--muted-foreground))] tracking-wider">
      {label}
    </div>
  </div>
);

const AvatarStack = () => {
  const items = [
    { bg: "rgba(212, 180, 106,0.9)", fg: "#050505", t: "K" },
    { bg: "rgba(255,255,255,0.9)", fg: "#050505", t: "J" },
    { bg: "rgba(120,180,255,0.85)", fg: "#050505", t: "W" },
    { bg: "rgba(255,180,100,0.85)", fg: "#050505", t: "L" },
  ];
  return (
    <div className="flex -space-x-2" aria-hidden>
      {items.map((it, i) => (
        <div
          key={i}
          className="w-5 h-5 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[9px] sm:text-[11px] font-bold border-2 border-[#050505]"
          style={{ background: it.bg, color: it.fg }}
        >
          {it.t}
        </div>
      ))}
    </div>
  );
};
